"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BRAND_IMPORT_API_MESSAGES = exports.BRAND_IMPORT_API_ERRORS = exports.BRAND_DOCUMENT_MIME_TYPES = exports.BRAND_IMPORT_POLL_CEILING_MS = exports.BRAND_IMPORT_STALE_AFTER_MS = exports.BRAND_IMPORT_MAX_PER_HOUR = exports.BRAND_IMPORT_MAX_ATTEMPTS = exports.WEB_PAGE_FAILURE_TEXT = exports.BRAND_IMPORT_ERROR_TEXT = exports.BRAND_IMPORT_PROGRESS_TEXT = exports.BRAND_IMPORT_PURPOSES = exports.BRAND_IMPORT_ACTIVE_STATUSES = exports.BRAND_IMPORT_STATUSES = void 0;
exports.notImportedReasonText = notImportedReasonText;
exports.brandImportPageView = brandImportPageView;
exports.summarizeImportDraft = summarizeImportDraft;
/**
 * Brand Workspace v2: the brand import job contract (final-design §2.4.3; import-engine §4).
 *
 * A job reads the customer's brand document (and, from Release 4, web pages) into a DRAFT. Nothing is
 * written to the workspace until the customer saves a wizard step (spec:331). The spec's fixed
 * sentences live here; the web builds its banners and summaries from them.
 *
 * Release 4 (final-design §9, lane R4-0) adds: the web pages of a job (a page's text is stored for the
 * server only, never in a view), Revisit's "Re-read my sources" start, and its Show changes view.
 */
const brand_fields_1 = require("./brand-fields");
exports.BRAND_IMPORT_STATUSES = ['queued', 'parsing', 'extracting', 'ready', 'partial', 'failed'];
exports.BRAND_IMPORT_ACTIVE_STATUSES = ['queued', 'parsing', 'extracting'];
/** Why an import runs: Brand Setup's first read, or Revisit Brand Setup's "Re-read my sources" (Release 4). */
exports.BRAND_IMPORT_PURPOSES = ['onboarding', 'reread'];
/** spec:381 */
exports.BRAND_IMPORT_PROGRESS_TEXT = 'Reading your brand guidelines. Fields will fill in as we go.';
exports.BRAND_IMPORT_ERROR_TEXT = {
    unsupported_file: "We couldn't open that file. Upload a .docx or PDF.", // spec:384
    no_text: "We couldn't find any text in that file. Try a text-based version.", // spec:385
    ai_failed: "We couldn't finish reading your document.", // spec:386 (with Try again)
    nothing_read: "We couldn't read any of your sources.", // R4: no source could be read
};
/** spec:366-368 */
exports.WEB_PAGE_FAILURE_TEXT = {
    unreachable: "We couldn't reach this page. Check the address and try again.",
    blocked: "This page can't be read automatically.",
    no_text: "We couldn't find any text on this page.",
};
exports.BRAND_IMPORT_MAX_ATTEMPTS = 5;
exports.BRAND_IMPORT_MAX_PER_HOUR = 20;
/** A job quiet this long is reported failed (interrupted): DesignJob's rule (design-generation.ts). */
exports.BRAND_IMPORT_STALE_AFTER_MS = 90_000;
/**
 * The web stops following an import still running after this long and offers Try again (web.md §5.4):
 * 13 minutes (10 until the API's deadline went to 660 s on 2026-10-31). A run ends by its deadline
 * (brand-import-runner.ts), or, when its replica stopped just before that, by the stale rule 90 s later:
 * both before this, so Try again is never refused as still running (brand-import-runner.spec.ts).
 */
exports.BRAND_IMPORT_POLL_CEILING_MS = 13 * 60_000;
exports.BRAND_DOCUMENT_MIME_TYPES = [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/pdf',
];
/** 40000 → "40,000", the same in every runtime (no toLocaleString). */
function formatCount(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
/** Why an item was not imported, in the words the summary's "Not imported" list shows (import-engine §3.10). */
function notImportedReasonText(item) {
    const detail = typeof item.detail === 'string' && item.detail.trim() !== '' ? item.detail.trim() : null;
    switch (item.reason) {
        case 'about_the_document':
            return 'About the document itself';
        case 'plans_or_next_steps':
            return 'Plans or next steps, not brand guidance';
        case 'no_matching_field':
            return 'No matching field';
        case 'other_brand':
            return detail ? `About another brand (${detail})` : 'About another brand';
        case 'covered_by_brand':
            return detail ? `Your brand's own content was used for ${detail}` : "Your brand's own content was used instead";
        case 'audience_limit':
            return `Limit of ${brand_fields_1.BRAND_LIMITS.audiences} audiences`; // spec:337, exact
        case 'list_limit':
            return `${detail ?? 'The list'} holds up to ${brand_fields_1.BRAND_LIMITS.tagsPerList} items`;
        case 'too_long':
            return detail ? `Too long for ${detail}` : 'Too long';
        case 'past_length_limit':
            return `Past the ${formatCount(brand_fields_1.BRAND_LIMITS.documentTextChars)}-character reading limit`;
        case 'page_not_read':
            if (detail && Object.prototype.hasOwnProperty.call(exports.WEB_PAGE_FAILURE_TEXT, detail)) {
                return exports.WEB_PAGE_FAILURE_TEXT[detail];
            }
            return detail ?? exports.WEB_PAGE_FAILURE_TEXT.unreachable;
        default:
            return 'No matching field';
    }
}
/** A stored page as a view shows it: never its text. */
function brandImportPageView(page) {
    return { url: page.url, scannedAt: page.scannedAt, status: page.status, reason: page.reason };
}
/**
 * What the import routes answer when they refuse (import-engine §5.2). The code rides in the body's
 * `error`, the message in `message`, which the web shows as it is. Access refusals use the brand API's
 * BRAND_NO_ACCESS and BRAND_ADMIN_ONLY (final-design §2.6), and a route while the flag is off answers
 * BRAND_WORKSPACE_V2_OFF.
 */
exports.BRAND_IMPORT_API_ERRORS = {
    NO_FILE: 'BRAND_IMPORT_NO_FILE',
    UNSUPPORTED_FILE: 'BRAND_IMPORT_UNSUPPORTED_FILE',
    /** Release 3 reads documents only: a request that names web pages is refused. Release 4 reads them. */
    PAGES_NOT_AVAILABLE: 'BRAND_IMPORT_PAGES_NOT_AVAILABLE',
    DOCUMENT_GONE: 'BRAND_DOCUMENT_GONE',
    DOCUMENT_UNAVAILABLE: 'BRAND_DOCUMENT_UNAVAILABLE',
    RUNNING: 'BRAND_IMPORT_RUNNING',
    NOT_RETRYABLE: 'BRAND_IMPORT_NOT_RETRYABLE',
    REPLACED: 'BRAND_IMPORT_REPLACED',
    RATE_LIMITED: 'BRAND_IMPORT_RATE_LIMITED',
    AI_UNAVAILABLE: 'BRAND_IMPORT_AI_UNAVAILABLE',
    /** Release 4: `urls` is not a JSON list of at most BRAND_LIMITS.urls valid public web addresses. 400. */
    URLS_INVALID: 'BRAND_IMPORT_URLS_INVALID',
    /** Release 4: a start with neither a document nor a web page to read. 400. */
    NO_SOURCE: 'BRAND_IMPORT_NO_SOURCE',
    /** Release 4, Re-read my sources: no stored document text or page text to read again. 409. */
    REREAD_NO_SOURCES: 'BRAND_REREAD_NO_SOURCES',
    /** Release 4: Show changes or Regenerate asked of an import that is not a re-read. 409. */
    NOT_A_REREAD: 'BRAND_IMPORT_NOT_A_REREAD',
    /** Release 4: Show changes asked of a re-read with no proposal yet (still running, or failed). 409. */
    REREAD_NOT_READY: 'BRAND_REREAD_NOT_READY',
    /** Release 4: Regenerate once the re-read has used its BRAND_IMPORT_MAX_ATTEMPTS attempts. 409. */
    REGENERATE_LIMIT: 'BRAND_REREAD_REGENERATE_LIMIT',
};
exports.BRAND_IMPORT_API_MESSAGES = {
    NO_FILE: 'Add a .docx or PDF to read.',
    UNSUPPORTED_FILE: exports.BRAND_IMPORT_ERROR_TEXT.unsupported_file,
    PAGES_NOT_AVAILABLE: "Scanning web pages isn't available yet.",
    NOT_FOUND: "That import doesn't exist.",
    DOCUMENT_GONE: 'This document was replaced by a newer upload.',
    DOCUMENT_UNAVAILABLE: "We couldn't open your document right now. Try again in a moment.",
    RUNNING: 'This import is still running.',
    NOT_RETRYABLE: "Try again won't help with this file. Upload a different file.",
    REPLACED: 'This import was replaced by a newer one.',
    RATE_LIMITED: `You've started ${exports.BRAND_IMPORT_MAX_PER_HOUR} imports for your brand in the last hour. Try again later.`,
    AI_UNAVAILABLE: "Reading documents needs the AI service, which isn't set up on this install.",
    URLS_INVALID: `Check the web page addresses. Add up to ${brand_fields_1.BRAND_LIMITS.urls} public pages, such as www.yourbrand.com.`,
    NO_SOURCE: 'Add your brand document or web pages to read.',
    REREAD_NO_SOURCES: "There's no brand document or web page to re-read. Choose Edit manually to update your brand's details.",
    NOT_A_REREAD: 'Only a re-read of your sources has changes to show.',
    REREAD_NOT_READY: "This re-read hasn't proposed any changes yet.",
    REGENERATE_LIMIT: "This re-read can't be regenerated again. Review its changes, or edit your brand's details yourself.",
};
/**
 * The counts behind "Filled 14 fields, 2 audiences and 6 guidelines. 3 sections were not imported."
 * (spec:351). `fields` counts every non-empty drafted field except Writing guidelines (the 13 text
 * fields, Value philosophy and the 5 Vocabulary lists, each list once); `guidelines` counts the
 * drafted Writing guidelines.
 */
function summarizeImportDraft(draft, notImported) {
    const fields = brand_fields_1.BRAND_FIELD_KEYS.filter((key) => key !== 'writingGuidelines' && !(0, brand_fields_1.isBrandValueEmpty)(draft.fields[key]?.value)).length;
    const guidelines = draft.fields.writingGuidelines?.value ?? [];
    return {
        fields,
        audiences: draft.audiences.length,
        guidelines: Array.isArray(guidelines) ? guidelines.length : 0,
        notImportedSections: notImported.length,
    };
}
//# sourceMappingURL=brand-import.js.map